/**
 * lib/api-client.ts
 *
 * Hono RPC client — provides a fully-typed API client generated from the
 * backend route types. Phase 2 will wire up the full RPC type export.
 *
 * For now this exports a simple fetch wrapper that attaches the Bearer token.
 */
import { getToken } from '../store/auth'

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787'

type RequestOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>
}

/**
 * Authenticated fetch wrapper.
 * Automatically attaches the Bearer token from localStorage if present.
 */
export const apiFetch = async <T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> => {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`)
  }

  return res.json() as Promise<T>
}
